import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'

import { Copy } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import { useCreateUsuarioAdmin } from '@/features/users/services'
import { RemovaCharEspeciais } from '@/utils/format'
import { FeedbackDialog } from '../../../components/form/feedback-dialog'
import WhatsAppInput from '../../../components/form/input/whatsApp-mask-input'
import CPFInput, { validaCPF } from '../../../components/form/input/cpf-mask-input'
import { StatusMapperInput } from '../../../components/form/input/StatusMapper'
import { DEFAULT_USER_PARTNERSHIP_CONTRACT } from '@/types/contrato-servico'
import { useCreateAgreementUser } from '@/features/agreement/services/useCreateAgreementUser'

// Define the form schema with zod
const formSchema = z.object({
  nome: z.string().min(1, { message: 'Nome é obrigatório' }),
  cpf: z
    .string()
    .min(11, { message: '' })
    .refine(val => validaCPF(val), { message: '' }),
  email: z.string().email({ message: 'Email inválido' }),
  password: z.string().min(6, { message: 'Senha deve ter ao menos 6 caracteres' }),
  whatsApp: z.string().min(10, { message: 'WhatsApp deve ter ao menos 10 dígitos' }),
  cargo: z.string({ required_error: 'Cargo é obrigatório' }),
  userType: z.string({ required_error: 'Tipo de usuário é obrigatório' })
})

type FormValues = z.infer<typeof formSchema>

export default function CreateAllUsuario() {
  const { mutate: createUsuario } = useCreateUsuarioAdmin()
  const { mutate: createAgreement } = useCreateAgreementUser()
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema)
  })

  // const [formData, setFormData] = useState(initialFormState)
  const [message, setMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)
  const [signLink, setSignLink] = useState<string | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [secondaryUsers, setSecondaryUsers] = useState<any[]>([])
  const [isMinutaSent, setIsMinutaSent] = useState(false)

  const resetStates = () => {
    form.reset()
    setIsMinutaSent(false)
    setSignLink(null)
    setMessage(null)
  }

  const handleCreateSecondaryUser = () => {
    setSecondaryUsers([...secondaryUsers, { ...form.getValues() }])
    resetStates()
    setMessage({ type: 'success', text: 'Novo usuário criado!' })
    setIsDialogOpen(true)
  }

  const handleSubmit = async (formData: FormValues) => {
    const cpfLimpo = RemovaCharEspeciais(formData.cpf)
    const info_user = {
      name: formData.nome,
      cpf: cpfLimpo,
      email: formData.email,
      password: formData.password,
      whatsApp: formData.whatsApp,
      typeCargo: formData.cargo,
      typeUser: formData.userType
    }

    console.log('ifo userrr', info_user)
    try {
      createUsuario(
        { info_user },
        {
          onSuccess: () => {
            createAgreement(
              { contracts: [DEFAULT_USER_PARTNERSHIP_CONTRACT], email: form.watch('email') },
              {
                onSuccess: () => {
                  setMessage({
                    type: 'success',
                    text: `Usuário e minuta criado com sucesso! Nome: ${info_user.name}, CPF: ${info_user.cpf}`
                  })
                  setIsDialogOpen(true)
                  setIsMinutaSent(true)
                },
                onError: error => {
                  console.error('Erro ao criar minuta:', error)
                  setMessage({
                    type: 'error',
                    text: 'Houve um erro ao criar a minuta.'
                  })
                  setIsDialogOpen(true)
                }
              }
            )
          },
          onError: error => {
            setMessage({
              type: 'error',
              text: `Erro ao criar usuário. Detalhes: ${error.message || 'Desconhecido'}`
            })
            setIsDialogOpen(true)
            console.error('Erro ao criar usuário:', error)
          }
        }
      )

      setMessage({ type: 'success', text: 'Usuário criado com sucesso!' })
      setIsDialogOpen(true)
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao criar usuário.' })
      setIsDialogOpen(true)
    }
  }

  const userType = useWatch({
    control: form.control,
    name: 'userType'
  })
  const cargosDisponiveis = userType === 'ADMIN' ? ['CEO', 'ANALISTA', 'FINANCEIRO', 'JURIDICO'] : ['CEO']

  const handleCopyLink = () => {
    if (signLink) {
      navigator.clipboard.writeText(signLink)
      setMessage({ type: 'success', text: 'Link copiado com sucesso!' })
    }
  }

  return (
    <>
      <Card className="w-full max-w-2xl mx-auto bg-background-secondary shadow-xl ">
        <CardHeader>
          <CardTitle>Cadastro de Usuário</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form id="createUserForm" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={form.control}
                  name="nome"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cpf"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CPF</FormLabel>
                      <FormControl>
                        <CPFInput value={field.value ?? ''} onCPFChange={field.onChange} aria-required />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" className="bg-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Senha</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="whatsApp"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>WhatsApp</FormLabel>
                      <FormControl>
                        <WhatsAppInput {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="userType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Usuário</FormLabel>
                      <FormControl>
                        <StatusMapperInput
                          value={field.value} // valor controlado
                          onChange={field.onChange} // avisa o RHF
                          enumType="typeUsers"
                          placeholder="Selecione o tipo de usuário"
                          aria-required
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cargo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cargo</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger className="bg-input text-foreground">
                            <SelectValue placeholder="Selecione o cargo" />
                          </SelectTrigger>
                          <SelectContent>
                            {cargosDisponiveis.map(role => (
                              <SelectItem key={role} value={role}>
                                {role}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full ">
            <Button
              type="submit"
              form="createUserForm"
              className="w-full flex bg-primary hover:bg-primary-hover text-primary-foreground"
            >
              Criar Usuário
            </Button>
          </div>

          <Button
            variant="outline"
            className="w-full flex justify-center bg-primary hover:bg-primary-hover text-primary-foreground"
            onClick={handleCreateSecondaryUser}
            disabled={!isMinutaSent}
          >
            Novo Usuário
          </Button>
        </CardFooter>
      </Card>

      <FeedbackDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} message={message} />
      {signLink && (
        <div className="mt-4">
          <Label htmlFor="signLink">Link para Assinatura</Label>
          <div className="flex space-x-2">
            <Input id="signLink" value={signLink} readOnly onClick={e => e.currentTarget.select()} />
            <Button variant="outline" className="flex items-center" onClick={handleCopyLink}>
              <Copy className="mr-2 h-4 w-4" />
              Copiar
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
